import { differenceInCalendarDays } from "date-fns";
import { formatDate, formatShortDate, formatTime } from "@/lib/formatDate";
import type { Reading, ReadingCode, ReadingType, TodayReadings } from "@/lib/api";
import { PAEDIATRIC_VITAL_RANGES, getPaediatricAgeGroup } from "@/lib/constants";

/** Entry order and labels for the vitals form; BP is entered as one "120/80" field */
export const VITAL_FIELDS: { key: "bp" | Exclude<ReadingCode, "bp_systolic" | "bp_diastolic">; label: string; unit: string; placeholder: string; step?: string }[] = [
    { key: "bp",     label: "Blood pressure", unit: "mmHg",  placeholder: "120/80" },
    { key: "pulse",  label: "Pulse",          unit: "bpm",   placeholder: "72" },
    { key: "temp",   label: "Temperature",    unit: "°F",    placeholder: "98.6", step: "0.1" },
    { key: "spo2",   label: "SpO2",           unit: "%",     placeholder: "98" },
    { key: "weight", label: "Weight",         unit: "kg",    placeholder: "0.0", step: "0.01" },
    { key: "height", label: "Height",         unit: "cm",    placeholder: "0",   step: "0.1" },
    { key: "rbs",    label: "Blood sugar (RBS)", unit: "mg/dL", placeholder: "110" },
];

/** Fallback ranges when the catalog hasn't loaded; mirrors observation_types */
export const FALLBACK_RANGES: Record<ReadingCode, [number, number]> = {
    bp_systolic: [40, 300], bp_diastolic: [20, 200], pulse: [20, 250], temp: [90, 110],
    spo2: [50, 100], weight: [0.3, 350], height: [20, 250], rbs: [10, 900],
};

export function parseBp(text: string): { sys: number; dia: number } | null {
    const m = text.trim().match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
    return m ? { sys: Number(m[1]), dia: Number(m[2]) } : null;
}

export function rangeOf(code: ReadingCode, types?: ReadingType[]): [number, number] {
    const t = types?.find(x => x.code === code);
    return t && t.min != null && t.max != null ? [t.min, t.max] : FALLBACK_RANGES[code];
}

/** Trim trailing zeros: 6.20 → "6.2", 98.0 → "98" */
export function fmtNum(v: number | null | undefined): string {
    if (v == null || !Number.isFinite(v)) return "";
    return String(Number(v.toFixed(2)));
}

export function fmtTime(iso: string | null | undefined): string {
    return formatTime(iso);
}

export function fmtDate(iso: string | null | undefined): string {
    return formatDate(iso);
}

/** "3 months ago", "12 days ago", "yesterday" — for last-weight hints */
export function ageOf(iso: string | null | undefined): string {
    if (!iso) return "";
    // Calendar days: a reading from last evening is "yesterday", not "today"
    const days = differenceInCalendarDays(new Date(), new Date(iso));
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 31) return `${days} days ago`;
    const months = Math.round(days / 30.4);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    const years = Math.round(days / 365);
    return `${years} year${years === 1 ? "" : "s"} ago`;
}

/** "Krisha (Nurse), 10:42" */
export function recordedByLine(r: Reading | undefined): string {
    if (!r) return "";
    const who = r.recordedBy ? `${r.recordedBy}${r.recordedByRole && r.recordedByRole !== "admin" ? ` (${cap(r.recordedByRole)})` : ""}` : "";
    return [who, fmtTime(r.effectiveAt)].filter(Boolean).join(", ");
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

/**
 * Today's readings as the prescription form's vitals fields (only what the form holds). With `since`,
 * only readings recorded after that time — so taking a newer set never undoes the doctor's own edits
 * to vitals that weren't re-measured.
 */
export function todayToPrescriptionVitals(t: TodayReadings["today"], since?: string | null): { bp?: string; pulse?: string; temp?: string; spo2?: string; weight?: string } {
    const out: Record<string, string> = {};
    const isNew = (r?: Reading) => !!r && (!since || r.recordedAt > since);
    if (t.bp_systolic && t.bp_diastolic && isNew(t.bp_systolic)) out.bp = `${fmtNum(t.bp_systolic.value)}/${fmtNum(t.bp_diastolic.value)}`;
    for (const code of ["pulse", "temp", "spo2", "weight"] as const) {
        if (isNew(t[code])) out[code] = fmtNum(t[code]!.value);
    }
    return out;
}

/** Newest recorded time among today's readings — changes when a new set is recorded */
export function latestRecordedAt(t: TodayReadings["today"] | undefined): string {
    if (!t) return "";
    return Object.values(t).reduce((max, r) => (r && r.recordedAt > max ? r.recordedAt : max), "");
}

export function bmi(weightKg?: number | null, heightCm?: number | null): number | null {
    if (!weightKg || !heightCm) return null;
    const m = heightCm / 100;
    return Math.round((weightKg / (m * m)) * 10) / 10;
}

/** Age in months from date of birth, else from age in years. Age 0 with no DOB means "not entered". */
export function patientAgeMonths(p?: { date_of_birth?: string | null; age?: number | null } | null): number | null {
    if (!p) return null;
    if (p.date_of_birth) {
        const b = new Date(p.date_of_birth);
        if (!Number.isNaN(b.getTime())) {
            const now = new Date();
            return Math.max(0, (now.getFullYear() - b.getFullYear()) * 12 + now.getMonth() - b.getMonth() - (now.getDate() < b.getDate() ? 1 : 0));
        }
    }
    return p.age ? Number(p.age) * 12 : null;
}

const CHILD_RANGE_KEY: Partial<Record<ReadingCode, "PULSE" | "BP_SYSTOLIC" | "SPO2" | "TEMPERATURE">> = {
    pulse: "PULSE", bp_systolic: "BP_SYSTOLIC", spo2: "SPO2", temp: "TEMPERATURE",
};

/**
 * H/L flag for the patient's age. The server flags against adult ranges; under 18 the age-banded
 * paediatric ranges are used instead, and readings with no paediatric range get no flag
 * (an infant's pulse of 130 is normal, not high).
 */
export function flagFor(r: Pick<Reading, "code" | "value" | "flag"> | null | undefined, ageMonths: number | null): "H" | "L" | null {
    if (!r) return null;
    if (ageMonths == null || ageMonths >= 216) return r.flag;
    if (r.code === "rbs") return r.flag;
    const key = CHILD_RANGE_KEY[r.code];
    if (!key) return null;
    const range = (PAEDIATRIC_VITAL_RANGES[getPaediatricAgeGroup(ageMonths)] as Record<string, { min: number; max: number }>)[key];
    if (!range) return null;
    return r.value > range.max ? "H" : r.value < range.min ? "L" : null;
}

/**
 * H/L flag for a value the doctor typed (not yet a saved reading): paediatric ranges under 18,
 * otherwise the catalog's adult reference range.
 */
export function flagValue(code: ReadingCode, value: number | null | undefined, ageMonths: number | null, types?: ReadingType[]): "H" | "L" | null {
    if (value == null || !Number.isFinite(value)) return null;
    const t = types?.find(x => x.code === code);
    let adult: "H" | "L" | null = null;
    if (t) adult = t.normalHigh != null && value > t.normalHigh ? "H" : t.normalLow != null && value < t.normalLow ? "L" : null;
    return flagFor({ code, value, flag: adult }, ageMonths);
}

/**
 * Text colour for a flagged value: red when high, and also when oxygen is low (low SpO2 is the
 * emergency); amber for other lows. Empty when not flagged.
 */
export function flagTone(flag: "H" | "L" | null | undefined, code?: string): string {
    if (!flag) return "";
    return flag === "H" || code === "spo2" ? "text-destructive" : "text-warning";
}

/** "12 Aug" (this year) or "12 Aug 2025" */
export function shortDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return sameYear ? formatShortDate(d) : formatDate(d);
}

const READING_ITEMS: { key: "bp" | Exclude<ReadingCode, "bp_systolic" | "bp_diastolic">; label: string; unit: string }[] = [
    { key: "bp", label: "BP", unit: "mmHg" },
    { key: "pulse", label: "Pulse", unit: "bpm" },
    { key: "spo2", label: "SpO2", unit: "%" },
    { key: "temp", label: "Temp", unit: "°F" },
    { key: "weight", label: "Weight", unit: "kg" },
    { key: "height", label: "Height", unit: "cm" },
    { key: "rbs", label: "RBS", unit: "mg/dL" },
];

/** "₹500 · Paid", "₹500 · ₹200 due", "₹500 · Cancelled" */
export function billText(inv: { total: number; paid: number; paymentStatus: string; cancelled: boolean }): string {
    const rupees = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    if (inv.cancelled) return `${rupees(inv.total)} · Cancelled`;
    if (inv.paymentStatus === "paid" || inv.paid >= inv.total - 0.01) return `${rupees(inv.total)} · Paid`;
    return `${rupees(inv.total)} · ${rupees(Math.max(0, inv.total - inv.paid))} due`;
}

/** Several bills on one visit as one line: "4 bills · ₹400 · ₹150 due" (cancelled bills left out) */
export function billSummary(invoices: { total: number; paid: number; cancelled: boolean }[]): string {
    const live = invoices.filter(i => !i.cancelled);
    const total = live.reduce((s, i) => s + i.total, 0);
    const due = live.reduce((s, i) => s + Math.max(0, i.total - i.paid), 0);
    const rupees = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    return `${live.length} bills · ${rupees(total)} · ${due > 0.01 ? `${rupees(due)} due` : "Paid"}`;
}

/** A visit's readings as display items ("BP 150/95 mmHg", H/L for the patient's age), BP as one item */
export function readingItems(readings: Reading[], ageMonths: number | null): { key: string; label: string; text: string; flag: "H" | "L" | null }[] {
    const get = (code: ReadingCode) => readings.find(r => r.code === code);
    return READING_ITEMS.map(item => {
        if (item.key === "bp") {
            const s = get("bp_systolic"), d = get("bp_diastolic");
            return s && d ? { key: "bp", label: item.label, text: `${fmtNum(s.value)}/${fmtNum(d.value)} ${item.unit}`, flag: flagFor(s, ageMonths) ?? flagFor(d, ageMonths) } : null;
        }
        const r = get(item.key);
        return r ? { key: item.key, label: item.label, text: `${fmtNum(r.value)} ${item.unit}`, flag: flagFor(r, ageMonths) } : null;
    }).filter(Boolean) as { key: string; label: string; text: string; flag: "H" | "L" | null }[];
}

/** Pallor / icterus / edema from a prescription's vitals as one line: "Pallor: Mild · No icterus, edema" */
export function examSigns(v: { pallor?: string; icterus?: string; edema?: string } | null | undefined): string {
  const signs = [["Pallor", v?.pallor], ["Icterus", v?.icterus], ["Edema", v?.edema]] as const;
  const found = signs.filter(([, s]) => s?.trim() && s.trim() !== "None").map(([label, s]) => `${label}: ${s!.trim()}`);
  const absent = signs.filter(([, s]) => s?.trim() === "None").map(([label]) => label.toLowerCase());
  return [...found, ...(absent.length ? [`No ${absent.join(", ")}`] : [])].join(" · ");
}
